import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCurrentUser, getUserFavorites, removeFromFavorites, updateClientProfile, getUserOrders, getClientProfile, getLoyaltyAccount, getLoyaltyPointsHistory, getRestaurantBannerUrl, getJpegFallbackUrl } from "../services/api";
import { Footer } from "../components/footer";
import NavBar from "../components/nav";
import DishCardUpdated from "../components/restaurant/DishCardUpdated";
import { X, Clock, CheckCircle, XCircle, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatPrice';
import namkeFallback from '../assets/namke-fallback.png';

export default function Profil() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [favLoading, setFavLoading] = useState(true);
  const [updatingFavorites, setUpdatingFavorites] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editProfileData, setEditProfileData] = useState(null);
  const [editProfileError, setEditProfileError] = useState("");
  const [editProfileSuccess, setEditProfileSuccess] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [clientProfile, setClientProfile] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const navigate = useNavigate();

  // Fonction pour basculer l'état d'expansion d'une commande
  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Vérifier l'authentification
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    console.log('Token présent:', !!token);
    if (!token) {
      console.log('Aucun token trouvé, redirection vers login');
      setError(t('profile.mustBeLoggedIn'));
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
  }, [navigate, t]);

  // Fonction pour recharger les favoris
  const reloadFavorites = async () => {
    try {
      setFavLoading(true);
      const favs = await getUserFavorites();
      setFavorites(favs.filter(fav => fav.favorited_object_type === 'dish'));
    } catch (err) {
      console.error('Erreur lors du rechargement des favoris:', err);
    } finally {
      setFavLoading(false);
    }
  };

  // Fonction pour supprimer un favori (plat ou restaurant)
  const handleRemoveFavorite = async (objectId, objectType = 'dish') => {
    try {
      setUpdatingFavorites(true);
      
      // Trouver le favori correspondant
      let favoriteToRemove;
      if (objectType === 'dish') {
        favoriteToRemove = favorites.find(fav => fav.favorited_object_id === objectId);
      } else {
        favoriteToRemove = favoriteRestaurants.find(fav => fav.favorited_object_id === objectId);
      }
      
      if (favoriteToRemove) {
        await removeFromFavorites(favoriteToRemove.id);
        
        // Supprimer immédiatement de l'état local
        if (objectType === 'dish') {
          setFavorites(prev => prev.filter(fav => fav.id !== favoriteToRemove.id));
        } else {
          setFavoriteRestaurants(prev => prev.filter(fav => fav.id !== favoriteToRemove.id));
        }
        
        // Afficher une notification de succès
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full';
        notification.textContent = objectType === 'dish' ? t('profile.dishRemovedFromFavorites') : t('profile.restaurantRemovedFromFavorites');
        document.body.appendChild(notification);
        
        // Animation d'entrée
        setTimeout(() => {
          notification.classList.remove('translate-x-full');
        }, 100);
        
        // Supprimer la notification après 3 secondes
        setTimeout(() => {
          notification.classList.add('translate-x-full');
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 3000);
        
      } else {
        console.error('Favori non trouvé');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du favori:', error);
      
      // Afficher une notification d'erreur
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full';
      notification.textContent = t('profile.favoriteRemoveError');
      document.body.appendChild(notification);
      
      // Animation d'entrée
      setTimeout(() => {
        notification.classList.remove('translate-x-full');
      }, 100);
      
      // Supprimer la notification après 3 secondes
      setTimeout(() => {
        notification.classList.add('translate-x-full');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 3000);
      
      // En cas d'erreur, recharger depuis le serveur
      await reloadFavorites();
    } finally {
      setUpdatingFavorites(false);
    }
  };

  useEffect(() => {
    async function fetchUser() {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setError(t('profile.mustBeLoggedIn'));
          setTimeout(() => navigate('/login'), 1500);
          return;
        }

        const data = await getCurrentUser();
        setUser(data);
        try {
          const profileData = await getClientProfile();
          setClientProfile(profileData || null);
        } catch (profileErr) {
          console.warn('Impossible de charger /profiles/client, fallback sur user.profile', profileErr);
          setClientProfile(null);
        }
      } catch (err) {
        console.error('Erreur lors du chargement du profil:', err);
        if (err.message.includes('401') || err.message.includes('Unauthorized') || err.message.includes('Token')) {
          // Token expiré ou invalide
          localStorage.removeItem('access_token');
          setError(t('profile.sessionExpired'));
          setTimeout(() => navigate('/login'), 2000);
        } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError(t('profile.connectionError'));
        } else {
          setError(t('profile.loadError'));
        }
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [navigate, t]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    const stored = localStorage.getItem(`profile_photo_${userId}`);
    if (stored) setProfilePhotoUrl(stored);
  }, [user?.id]);

  useEffect(() => {
    async function fetchFavorites() {
      try {
        // Vérifier si l'utilisateur est authentifié
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.log('Aucun token trouvé, impossible de charger les favoris');
          setFavLoading(false);
          return;
        }

        const favs = await getUserFavorites();
        setFavorites(favs.filter(fav => fav.favorited_object_type === 'dish'));
        setFavoriteRestaurants(favs.filter(fav => fav.favorited_object_type === 'restaurant'));
      } catch (err) {
        console.error('Erreur lors du chargement des favoris:', err);
        
        // Si c'est une erreur d'authentification, rediriger vers la connexion
        if (err.message === 'Token expiré ou invalide') {
          console.log('Token expiré, redirection vers la connexion');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          navigate('/login');
          return;
        }
        
        // Pour les autres erreurs, afficher un message
        setError(t('profile.favoritesLoadError'));
      } finally {
        setFavLoading(false);
      }
    }
    fetchFavorites();
  }, [navigate, t]);

  useEffect(() => {
    async function fetchOrders() {
      try {
        setOrdersLoading(true);
        const userOrders = await getUserOrders();
        setOrders(userOrders);
      } catch (err) {
        console.error('Erreur lors du chargement des commandes:', err);
      } finally {
        setOrdersLoading(false);
      }
    }
    fetchOrders();
  }, []);

  useEffect(() => {
    async function fetchLoyalty() {
      try {
        const [account, history] = await Promise.all([
          getLoyaltyAccount(),
          getLoyaltyPointsHistory({ limit: 5 }),
        ]);
        setLoyaltyAccount(account || null);
        setLoyaltyHistory(Array.isArray(history) ? history : []);
      } catch (err) {
        setLoyaltyAccount(null);
        setLoyaltyHistory([]);
      }
    }
    fetchLoyalty();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse space-y-6">
          {/* Profile header card */}
          <div className="bg-white rounded-2xl p-6 flex items-center gap-5 border border-gray-100">
            <div className="w-20 h-20 rounded-full bg-gray-200 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-gray-200 rounded w-1/3" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-4 bg-gray-200 rounded w-1/4" />
            </div>
            <div className="h-9 w-24 bg-gray-200 rounded-lg" />
          </div>
          {/* Loyalty stat tiles */}
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-7 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
          {/* Favourites dish grid */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="h-5 bg-gray-200 rounded w-1/4 mb-4" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-gray-100">
                  <div className="h-32 bg-gray-200" />
                  <div className="p-2 space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Order list */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 space-y-3">
            <div className="h-5 bg-gray-200 rounded w-1/4 mb-2" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>
    );
  }

  // On suppose que le profil client est dans user.client_profile ou user.profile
  const profile = clientProfile || user?.client_profile || user?.profile || {};

  const getProfileImageSrc = () => {
    return (
      profilePhotoUrl ||
      profile?.photo_url ||
      profile?.image_url ||
      profile?.avatar_url ||
      profile?.banner_image_url ||
      `https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=${profile?.first_name || 'user'}`
    );
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setEditProfileError(t('profile.invalidImage'));
      return;
    }
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setEditProfileError(t('profile.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && user?.id) {
        localStorage.setItem(`profile_photo_${user.id}`, result);
        setProfilePhotoUrl(result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Handler ouverture popup
  const handleEditProfileClick = () => {
    setEditProfileData({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      email: user?.email || '',
      phone: profile?.phone || '',
      birth_date: profile?.birth_date || '',
      address: profile?.address || '',
    });
    setShowEditProfile(true);
  };

  // Handler changement champ
  const handleEditProfileChange = (e) => {
    const { name, value } = e.target;
    setEditProfileData(prev => ({ ...prev, [name]: value }));
  };

  // Handler submit (à compléter avec appel API si besoin)
  const handleEditProfileSubmit = async (e) => {
    e.preventDefault();
    setEditProfileError("");
    setEditProfileSuccess("");
    // Validations simples (email non modifiable via cette API)
    if (!editProfileData.first_name?.trim() || !editProfileData.last_name?.trim()) {
      setEditProfileError(t('profile.firstLastNameRequired'));
      return;
    }
    try {
      // L'API PUT /profiles/client n'accepte que first_name, last_name, phone, birth_date, address (pas email)
      const payload = {
        first_name: editProfileData.first_name?.trim() || null,
        last_name: editProfileData.last_name?.trim() || null,
        phone: editProfileData.phone?.trim() || null,
        birth_date: editProfileData.birth_date || null,
        address: editProfileData.address?.trim() || null,
      };
      await updateClientProfile(payload);
      setEditProfileSuccess(t('profile.updateSuccess'));
      setShowEditProfile(false);
      // Recharger le user + client profile
      const [data, refreshedProfile] = await Promise.all([
        getCurrentUser(),
        getClientProfile().catch(() => null),
      ]);
      setUser(data);
      if (refreshedProfile) setClientProfile(refreshedProfile);
    } catch (err) {
      setEditProfileError(t('profile.updateError'));
    }
  };

  return (
    <>
      <NavBar login={true} />
      <div className="min-h-screen bg-gray-50 pt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <section id="profile-info" className="bg-white rounded-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="shrink-0">
            <img
              src={getProfileImageSrc()}
              alt={t('profile.photoAlt')}
              className="w-24 h-24 rounded-full border-4 border-neutral-200 object-cover"
              onError={(e) => {
                // En cas d'erreur de chargement de l'image, utiliser l'image par défaut
                e.target.src = `https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=${profile?.first_name || 'user'}`;
              }}
            />
            <label className="mt-3 inline-flex items-center px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-md cursor-pointer transition-colors">
              {t('profile.changePhoto')}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePhotoChange}
              />
            </label>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl mb-2 text-neutral-900 break-words">
                {(profile?.first_name || t('profile.firstNamePlaceholder'))} {(profile?.last_name || t('profile.lastNamePlaceholder'))}
              </h1>
              <p className="text-neutral-600 mb-2 break-all">{user?.email || <span className="italic text-gray-400">{t('profile.emailNotProvided')}</span>}</p>
              <p className="text-neutral-600 break-words">{profile?.phone || <span className="italic text-gray-400">{t('profile.notProvided')}</span>}</p>
              <p className="text-neutral-600">
                {profile?.birth_date ? new Date(profile.birth_date).toLocaleDateString() : <span className="italic text-gray-400">{t('profile.birthDateNotProvided')}</span>}
              </p>
              <p className="text-neutral-600 break-words">
                {profile?.address || <span className="italic text-gray-400">{t('profile.addressNotProvided')}</span>}
              </p>
            </div>
            <button className="sm:ml-auto px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 h-fit" onClick={handleEditProfileClick}>
              <i className="fa-regular fa-pen-to-square mr-2"></i>{t('profile.editProfile')}
            </button>
          </div>
        </section>

        <section id="loyalty" className="bg-white rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4 text-neutral-900">{t('profile.loyaltyProgram')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-700">{t('profile.level')}</p>
              <p className="text-2xl font-bold text-amber-900 uppercase">{loyaltyAccount?.level || 'bronze'}</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm text-emerald-700">{t('profile.availablePoints')}</p>
              <p className="text-2xl font-bold text-emerald-900">{loyaltyAccount?.available_points ?? 0}</p>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <p className="text-sm text-sky-700">{t('profile.totalPoints')}</p>
              <p className="text-2xl font-bold text-sky-900">{loyaltyAccount?.total_points ?? 0}</p>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">{t('profile.latestOperations')}</p>
            {loyaltyHistory.length === 0 ? (
              <p className="text-sm text-gray-500">{t('profile.noPointsOperations')}</p>
            ) : (
              <div className="space-y-2">
                {loyaltyHistory.map((item) => (
                  <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                    <p className="text-sm text-gray-700">{item.reason}</p>
                    <p className={`text-sm font-semibold ${item.points >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {item.points >= 0 ? `+${item.points}` : item.points}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
                {/* Favoris plats */}
        <section id="favorites-dishes" className="bg-white rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-6 text-neutral-900">{t('profile.myFavoriteDishes')}</h2>
                    {favLoading ? (
            <div className="animate-pulse grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-gray-100">
                  <div className="h-32 bg-gray-200" />
                  <div className="p-2 space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('profile.noFavoriteDishesYet')}</div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${updatingFavorites ? 'pointer-events-none opacity-50' : ''}`}>
              {updatingFavorites && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">{t('profile.updating')}</p>
                  </div>
                </div>
              )}
              {favorites.map(fav => fav.favorited_object && (
                <div key={fav.id} className="relative group">
                  <DishCardUpdated
                    dish={fav.favorited_object}
                    forceFavoriteState={true}
                    onAddToCart={(dish) => {
                      // Ajouter le plat au panier
                      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
                      const existingItem = cart.find(item => item.id === dish.id);
                      
                      if (existingItem) {
                        existingItem.quantity += 1;
                      } else {
                        cart.push({
                          ...dish,
                          quantity: 1,
                          restaurant_id: dish.restaurant_id || dish.restaurant?.id
                        });
                      }
                      
                      localStorage.setItem('cart', JSON.stringify(cart));
                      
                      // Afficher une notification plus élégante
                      const notification = document.createElement('div');
                      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full';
                      notification.textContent = t('profile.addedToCart', { name: dish.name });
                      document.body.appendChild(notification);
                      
                      // Animation d'entrée
                      setTimeout(() => {
                        notification.classList.remove('translate-x-full');
                      }, 100);
                      
                      // Supprimer la notification après 3 secondes
                      setTimeout(() => {
                        notification.classList.add('translate-x-full');
                        setTimeout(() => {
                          document.body.removeChild(notification);
                        }, 300);
                      }, 3000);
                    }}
                    onFavoriteToggle={async (dishId, isFavorite) => {
                      if (!isFavorite) {
                        // Si le plat n'est plus favori, le supprimer de la liste
                        await handleRemoveFavorite(dishId);
                      }
                    }}
                  />
                  {/* Bouton de suppression des favoris */}
                  <button
                    onClick={() => handleRemoveFavorite(fav.favorited_object.id, 'dish')}
                    disabled={updatingFavorites}
                    className="absolute top-2 left-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('profile.removeFromFavorites')}
                  >
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Commandes */}
        <section id="orders" className="bg-white rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-6 text-neutral-900">{t('profile.myOrders')}</h2>
          {ordersLoading ? (
            <div className="animate-pulse space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-xl" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('profile.noOrdersYet')}</div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="border border-gray-200 rounded-lg overflow-hidden transition-shadow">
                  {/* En-tête de la commande (toujours visible) */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleOrderExpansion(order.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-neutral-900">
                          {t('profile.orderNumber', { id: order.id })}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {order.status === 'pending' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                              <Clock size={14} />
                              {t('profile.orderStatus.pending')}
                            </span>
                          )}
                          {order.status === 'confirmed' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                              <Package size={14} />
                              {t('profile.orderStatus.confirmed')}
                            </span>
                          )}
                          {order.status === 'ready' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                              <CheckCircle size={14} />
                              {t('profile.orderStatus.ready')}
                            </span>
                          )}
                          {order.status === 'delivered' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                              <CheckCircle size={14} />
                              {t('profile.orderStatus.delivered')}
                            </span>
                          )}
                          {order.status === 'cancelled' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                              <XCircle size={14} />
                              {t('profile.orderStatus.cancelled')}
                            </span>
                          )}
                        </div>
                        {/* Icône d'expansion */}
                        <div className="flex-shrink-0">
                          {expandedOrders.has(order.id) ? (
                            <ChevronUp size={20} className="text-gray-500" />
                          ) : (
                            <ChevronDown size={20} className="text-gray-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contenu détaillé de la commande (visible seulement si étendu) */}
                  {expandedOrders.has(order.id) && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-4">
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">{t('profile.orderDetail.restaurant')}</span> {order.restaurant?.name || t('profile.orderDetail.unknownRestaurant')}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">{t('profile.orderDetail.total')}</span> {formatCurrency(order.total_amount || 0)}
                        </p>
                        {order.address && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">{t('profile.orderDetail.deliveryAddress')}</span> {order.address}
                          </p>
                        )}
                        {order.phone && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">{t('profile.orderDetail.phone')}</span> {order.phone}
                          </p>
                        )}
                      </div>

                      {order.order_items && order.order_items.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="font-medium text-sm text-gray-700 mb-3">{t('profile.orderDetail.itemsOrdered')}</h4>
                          <div className="space-y-3">
                            {order.order_items.map((item, index) => (
                              <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100">
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-800">
                                    {item.quantity}x {item.dish?.name || t('profile.orderDetail.unknownDish')}
                                  </span>
                                  {item.dish?.description && (
                                    <p className="text-xs text-gray-500 mt-1">{item.dish.description}</p>
                                  )}
                                </div>
                                <span className="text-sm font-semibold text-gray-900">
                                  {formatCurrency(item.price || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Restaurants favoris */}
        <section id="favorite-restaurants" className="bg-white rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-6 text-neutral-900">{t('profile.myFavoriteRestaurants')}</h2>
          {favLoading ? (
            <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-gray-100">
                  <div className="h-36 bg-gray-200" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : favoriteRestaurants.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('profile.noFavoriteRestaurantsYet')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favoriteRestaurants.map((fav) => fav.favorited_object && (
                <div key={fav.id} className="relative group">
                  <div 
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 cursor-pointer transform hover:scale-105"
                    onClick={() => navigate(`/restaurant/${fav.favorited_object.id}`)}
                  >
                    <div className="h-48 bg-gray-200 flex items-center justify-center">
                      {getRestaurantBannerUrl(fav.favorited_object) ? (
                        <img 
                          src={getRestaurantBannerUrl(fav.favorited_object)}
                          alt={fav.favorited_object.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const fallback = getJpegFallbackUrl(getRestaurantBannerUrl(fav.favorited_object));
                            if (fallback && e.currentTarget.dataset.fallbackApplied !== '1') {
                              e.currentTarget.dataset.fallbackApplied = '1';
                              e.currentTarget.src = fallback;
                              return;
                            }
                            e.currentTarget.src = namkeFallback;
                          }}
                        />
                      ) : (
                        <div className="text-gray-400 text-center">
                          <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                          <p className="text-sm">{t('profile.noImage')}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg text-neutral-900 mb-2">
                        {fav.favorited_object.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {fav.favorited_object.description || t('profile.noDescriptionAvailable')}
                      </p>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span>{fav.favorited_object.rating || 'N/A'}</span>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation(); // Empêcher la navigation vers le restaurant
                            handleRemoveFavorite(fav.favorited_object.id, 'restaurant');
                          }}
                          className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1 rounded-md hover:bg-red-50 transition-colors"
                        >
                          {t('profile.remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        </div>
      </div>
      {/* Popup édition profil */}
      {showEditProfile && editProfileData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative">
            <button onClick={() => setShowEditProfile(false)} className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200">
              <X size={22} />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-center">{t('profile.editProfileTitle')}</h2>
            <form onSubmit={handleEditProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" name="first_name" value={editProfileData.first_name} onChange={handleEditProfileChange} placeholder={t('profile.firstNamePlaceholder')} className="w-full min-w-0 border rounded-lg px-4 py-2" required />
                <input type="text" name="last_name" value={editProfileData.last_name} onChange={handleEditProfileChange} placeholder={t('profile.lastNamePlaceholder')} className="w-full min-w-0 border rounded-lg px-4 py-2" required />
              </div>
              <input type="email" name="email" value={editProfileData.email} onChange={handleEditProfileChange} placeholder={t('profile.emailPlaceholder')} className="w-full border rounded-lg px-4 py-2" required />
              <input type="tel" name="phone" value={editProfileData.phone} onChange={handleEditProfileChange} placeholder={t('profile.phonePlaceholder')} className="w-full border rounded-lg px-4 py-2" />
              <input type="date" name="birth_date" value={editProfileData.birth_date} onChange={handleEditProfileChange} placeholder={t('profile.birthDatePlaceholder')} className="w-full border rounded-lg px-4 py-2" />
              <input type="text" name="address" value={editProfileData.address} onChange={handleEditProfileChange} placeholder={t('profile.addressPlaceholder')} className="w-full border rounded-lg px-4 py-2" />
              {editProfileError && <div className="text-red-600 text-sm">{editProfileError}</div>}
              {editProfileSuccess && <div className="text-[rgb(5, 70, 37)] text-sm">{editProfileSuccess}</div>}
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowEditProfile(false)} className="px-6 py-2 rounded-lg border border-gray-300 bg-gray-100 hover:bg-gray-200">{t('profile.cancel')}</button>
                <button type="submit" className="px-6 py-2 rounded-lg bg-[rgb(5, 70, 37)] text-white hover:bg-green-700">{t('profile.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}